<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { ORDER_SOURCES } from 'dashboard/routes/dashboard/webOrder/helpers/orderFilters';

import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CsatEmptyState from '../CsatEmptyState.vue';
import {
  ORIGIN_COUNT_COLUMNS,
  ORIGIN_RATE_COLUMNS,
  PAID_DUE_COLUMNS,
  PRODUCTION_BASE_COLUMNS,
  DELIVERY_SHIPMENT_LEAD,
  DELIVERY_SHIPMENT_METRICS,
  customerLeadColumns,
  customerMetricColumns,
  dateBasisLabelKey,
  formatLocalDateTimeKey,
  inventoryLeadColumns,
  inventoryMetricColumns,
  showsEmployeeSearch,
  showsOrderSources,
  showsPartnerFilter,
  showsStatusFilter,
  showsWarehouseFilter,
  subtypeLabelKey,
  userDetailColumns,
  userPreviewColumns,
} from '../../helpers/downloadReportCatalog';

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: 'idle',
    validator: value =>
      ['idle', 'coming_soon', 'empty', 'error', 'table'].includes(value),
  },
  payload: {
    type: Object,
    default: null,
  },
  errorMessage: {
    type: String,
    default: '',
  },
  reportType: {
    type: String,
    default: '',
  },
  reportSubtype: {
    type: String,
    default: '',
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['pageChange']);
const { t, te } = useI18n();

const rows = computed(() => props.payload?.rows || []);
const totals = computed(() => props.payload?.totals || {});
const meta = computed(() => props.payload?.meta || {});
const currentPage = computed(() => Number(meta.value.page) || 1);
const perPage = computed(() => Number(meta.value.per_page) || 50);
const totalRows = computed(() => Number(meta.value.total_rows) || 0);
const showPagination = computed(() => totalRows.value > perPage.value);
const isUserReport = computed(() => props.payload?.report_type === 'user');
const isUserDetailReport = computed(
  () =>
    isUserReport.value &&
    props.payload?.report_layout === 'details' &&
    ['cross_sale_upsale_approve', 'order_creation'].includes(
      props.payload?.report_subtype
    )
);
const isPaidDueReport = computed(
  () => props.payload?.report_subtype === 'paid_and_due_amount'
);
const isProductionReport = computed(
  () => props.payload?.report_type === 'production'
);
const isDailyProductReport = computed(
  () =>
    isProductionReport.value &&
    props.payload?.report_subtype === 'daily_product_wise_sales'
);
const isCustomerReport = computed(
  () => props.payload?.report_type === 'customer'
);
const isInventoryReport = computed(
  () => props.payload?.report_type === 'inventory'
);
const isDeliveryReport = computed(
  () => props.payload?.report_type === 'delivery_partner'
);
const inventoryLead = computed(() =>
  inventoryLeadColumns(props.payload?.report_subtype)
);
const inventoryMetrics = computed(() =>
  inventoryMetricColumns(props.payload?.report_subtype)
);
const customerLead = computed(() =>
  customerLeadColumns(props.payload?.report_subtype)
);
const customerMetrics = computed(() =>
  customerMetricColumns(props.payload?.report_subtype)
);
const productionLeadColumns = computed(() => ['sku', 'product_name']);
const productionDateLabel = computed(() => {
  const basis = props.payload?.date_basis;
  if (basis === 'approved')
    return t('DOWNLOAD_REPORTS.FORM.DATE_RANGE_APPROVAL');
  if (basis === 'in_transit') {
    return t('DOWNLOAD_REPORTS.FORM.DATE_BASIS_OPTIONS.IN_TRANSIT');
  }
  if (basis === 'delivered') {
    return t('DOWNLOAD_REPORTS.FORM.DATE_BASIS_OPTIONS.DELIVERED');
  }
  if (basis === 'creation')
    return t('DOWNLOAD_REPORTS.FORM.DATE_RANGE_CREATION');
  return t('DOWNLOAD_REPORTS.TABLE.DATE');
});
const originMetricColumns = computed(() => [
  ...ORIGIN_COUNT_COLUMNS.flatMap(column => [column, `${column}_amount`]),
  ...ORIGIN_RATE_COLUMNS,
]);
const activeType = computed(
  () => props.payload?.report_type || props.reportType
);
const activeSubtype = computed(
  () => props.payload?.report_subtype || props.reportSubtype
);
const limitNote = computed(() => {
  if (!['table', 'empty'].includes(props.status)) return '';
  const parts = [];
  if (meta.value.range_clamped) {
    parts.push(t('DOWNLOAD_REPORTS.PREVIEW.CLAMPED'));
  }
  if (meta.value.truncated) {
    parts.push(
      t(
        isUserDetailReport.value
          ? 'DOWNLOAD_REPORTS.PREVIEW.TRUNCATED_ORDERS'
          : 'DOWNLOAD_REPORTS.PREVIEW.TRUNCATED'
      )
    );
  }
  if (!parts.length) return '';
  return `${parts.join(' ')} ${t('DOWNLOAD_REPORTS.PREVIEW.ON_SCREEN_CSV')}`;
});
const cacheNote = computed(() => {
  if (!['table', 'empty'].includes(props.status) || !meta.value.cached) {
    return '';
  }
  return t('DOWNLOAD_REPORTS.PREVIEW.CACHED');
});
const userColumns = computed(() =>
  isUserReport.value && !isUserDetailReport.value
    ? userPreviewColumns(props.payload?.report_subtype)
    : []
);
const userDetailCols = computed(() =>
  isUserDetailReport.value
    ? userDetailColumns(props.payload?.report_subtype)
    : []
);

const USER_DETAIL_TEXT = new Set(['invoice_number', 'agent_name']);
const USER_LINE_TEXT = new Set([
  'sale_date',
  'invoice_number',
  'order_source',
  'sku',
  'product_name',
  'status',
]);
const USER_DETAIL_LABELS = {
  invoice_number: 'INVOICE',
  agent_name: 'AGENT',
};

const USER_AMOUNT_KEYS = new Set([
  'assigned_amount',
  'upsale_amount',
  'approved_amount',
]);

const PAID_DUE_AMOUNT_KEYS = new Set(['total', 'advance', 'paid', 'due']);

const formatBoundary = value => {
  const key = formatLocalDateTimeKey(value);
  return key ? key.slice(0, 16).replace('T', ' ') : value || '';
};

const previewTitle = computed(() => {
  if (!props.payload) return '';
  const reportName = t(subtypeLabelKey(props.payload.report_subtype));
  const from = formatBoundary(props.payload.from);
  const to = formatBoundary(props.payload.to);
  if (!props.payload.date_basis) {
    return t('DOWNLOAD_REPORTS.PREVIEW.TITLE_RANGE', { reportName, from, to });
  }
  return t('DOWNLOAD_REPORTS.PREVIEW.TITLE', {
    reportName,
    dateBasis: t(dateBasisLabelKey(props.payload.date_basis)),
    from,
    to,
  });
});

const originLabel = origin => {
  if (props.payload?.report_mode === 'attribution') return origin || '';
  const key = `WEB_ORDER.FILTERS.ORDER_SOURCES.${String(origin || '').toUpperCase()}`;
  return te(key) ? t(key) : origin || '';
};

const formatCount = value => {
  const amount = Number(value);
  if (Number.isNaN(amount)) return '0';
  return amount.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const formatAmount = value => {
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return (0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatUserCell = (row, column) => {
  if (USER_LINE_TEXT.has(column)) return row?.[column] || '';
  if (
    USER_AMOUNT_KEYS.has(column) ||
    column === 'discount' ||
    column === 'amount'
  ) {
    return formatAmount(row?.[column]);
  }
  return formatCount(row?.[column]);
};

const userCellClass = column =>
  USER_LINE_TEXT.has(column)
    ? 'whitespace-nowrap px-5 py-3 text-left text-sm text-n-slate-12'
    : 'whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12';

const originMetricLabel = column => {
  if (column.endsWith('_amount')) {
    const status = column.slice(0, -'_amount'.length).toUpperCase();
    return `${t(`DOWNLOAD_REPORTS.TABLE.${status}`)} ${t('DOWNLOAD_REPORTS.TABLE.AMOUNT')}`;
  }
  return t(`DOWNLOAD_REPORTS.TABLE.${String(column).toUpperCase()}`);
};

const originRate = (row, column) => {
  const orders = Number(row?.orders) || 0;
  const status = {
    delivery_rate: 'delivered',
    return_rate: 'returned',
    cancelled_rate: 'cancelled',
  }[column];
  if (!orders) return '0%';
  const share = ((Number(row?.[status]) || 0) / orders) * 100;
  return `${share.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
};

const formatOriginMetric = (row, column) => {
  if (column.endsWith('_rate')) return originRate(row, column);
  if (column.endsWith('_amount') || column === 'amount')
    return formatAmount(row?.[column]);
  return formatCount(row?.[column]);
};

const productionMetricLabel = column =>
  t(`DOWNLOAD_REPORTS.TABLE.${String(column).toUpperCase()}`);

const customerMetricLabel = column => {
  if (activeSubtype.value === 'top_customer_repeat' && column === 'orders') {
    return t('DOWNLOAD_REPORTS.TABLE.ORDER_FREQUENCY');
  }
  if (activeSubtype.value === 'top_customer_repeat' && column === 'amount') {
    return t('DOWNLOAD_REPORTS.TABLE.ORDER_VALUE');
  }
  return productionMetricLabel(column);
};

const detailColumnLabel = column => {
  const key = USER_DETAIL_LABELS[column] || String(column).toUpperCase();
  return t(`DOWNLOAD_REPORTS.TABLE.${key}`);
};

const formatDetailCell = (row, column) => {
  if (USER_DETAIL_TEXT.has(column)) return row?.[column] || '';
  if (column === 'amount') return formatAmount(row?.[column]);
  return formatCount(row?.[column]);
};

const formatDetailTotal = (column, index) => {
  if (USER_DETAIL_TEXT.has(column)) {
    return index === 0 ? t('DOWNLOAD_REPORTS.TABLE.TOTALS') : '';
  }
  return formatDetailCell(totals.value, column);
};

const userColumnLabel = column =>
  t(`DOWNLOAD_REPORTS.TABLE.${String(column).toUpperCase()}`);

const paidDueColumnLabel = column => {
  if (column === 'total') return t('DOWNLOAD_REPORTS.TABLE.ORDER_TOTAL');
  return t(`DOWNLOAD_REPORTS.TABLE.${String(column).toUpperCase()}`);
};

const paymentLabel = payment =>
  t(`DOWNLOAD_REPORTS.TABLE.PAYMENT_${String(payment || '').toUpperCase()}`);

const formatPaidDueCell = (row, column) => {
  if (PAID_DUE_AMOUNT_KEYS.has(column)) return formatAmount(row?.[column]);
  return formatCount(row?.[column]);
};

const PRODUCTION_LEAD_LABELS = {
  sku: 'DOWNLOAD_REPORTS.TABLE.SKU',
  product_name: 'DOWNLOAD_REPORTS.TABLE.PRODUCT',
};

const productionLeadLabel = column => t(PRODUCTION_LEAD_LABELS[column]);

const orderSourceLabel = source => {
  const key = `WEB_ORDER.FILTERS.ORDER_SOURCES.${String(source).toUpperCase()}`;
  return te(key) ? t(key) : source;
};

const sourceQty = (row, source) => formatCount(row?.sources?.[source] || 0);

const formatDailyMoney = value =>
  value === '' || value === null || value === undefined
    ? ''
    : formatAmount(value);

const formatProductionCell = (row, column) =>
  column === 'amount'
    ? formatAmount(row?.[column])
    : formatCount(row?.[column]);

const CUSTOMER_LEAD_LABELS = {
  customer_name: 'DOWNLOAD_REPORTS.TABLE.CUSTOMER',
  phone: 'DOWNLOAD_REPORTS.TABLE.PHONE',
  product_name: 'DOWNLOAD_REPORTS.TABLE.PRODUCT',
};

const customerLeadLabel = column => t(CUSTOMER_LEAD_LABELS[column]);

const formatCustomerCell = (row, column) =>
  column === 'amount'
    ? formatAmount(row?.[column])
    : formatCount(row?.[column]);

const INVENTORY_LEAD_LABELS = {
  product_name: 'DOWNLOAD_REPORTS.TABLE.PRODUCT',
  sku: 'DOWNLOAD_REPORTS.TABLE.SKU',
  expires_on: 'DOWNLOAD_REPORTS.TABLE.EXPIRES',
  sale_date: 'DOWNLOAD_REPORTS.TABLE.DATE',
  category: 'DOWNLOAD_REPORTS.TABLE.CATEGORY',
};

const inventoryLeadLabel = column => t(INVENTORY_LEAD_LABELS[column]);

const inventoryMetricLabel = column =>
  t(`DOWNLOAD_REPORTS.TABLE.${String(column).toUpperCase()}`);

const DELIVERY_LEAD_LABELS = {
  partner_name: 'DOWNLOAD_REPORTS.TABLE.PARTNER',
  provider: 'DOWNLOAD_REPORTS.TABLE.PROVIDER',
};

const deliveryLeadLabel = column => t(DELIVERY_LEAD_LABELS[column]);

const deliveryMetricLabel = column =>
  column === 'cancelled'
    ? t('DOWNLOAD_REPORTS.TABLE.CANCELLED_RETURNED')
    : t(`DOWNLOAD_REPORTS.TABLE.${String(column).toUpperCase()}`);

const filterOn = key => Boolean(props.filters?.[key]);

const emptyPreviewCopy = () => {
  const type = activeType.value;
  const subtype = activeSubtype.value;
  const warehouse =
    showsWarehouseFilter(type, subtype) && filterOn('warehouse');
  const partners = showsPartnerFilter(type, subtype) && filterOn('partners');
  const statuses = showsStatusFilter(type, subtype) && filterOn('statuses');
  const sources = showsOrderSources(type, subtype) && filterOn('sources');
  const employees = showsEmployeeSearch(type) && filterOn('employees');

  if (subtype === 'stock_summary') {
    return {
      title: t('DOWNLOAD_REPORTS.PREVIEW.EMPTY_STOCK_TITLE'),
      description: t(
        warehouse
          ? 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_STOCK_WAREHOUSE'
          : 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_STOCK'
      ),
    };
  }
  if (subtype === 'damage') {
    return {
      title: t('DOWNLOAD_REPORTS.PREVIEW.EMPTY'),
      description: t(
        warehouse
          ? 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_DAMAGE_WAREHOUSE'
          : 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_DAMAGE'
      ),
    };
  }
  if (subtype === 'expiries') {
    return {
      title: t('DOWNLOAD_REPORTS.PREVIEW.EMPTY'),
      description: t(
        warehouse
          ? 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_EXPIRY_WAREHOUSE'
          : 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_EXPIRY'
      ),
    };
  }
  if (subtype === 'daily_stock_shortage') {
    return {
      title: t('DOWNLOAD_REPORTS.PREVIEW.EMPTY'),
      description: t(
        warehouse
          ? 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_SHORTAGE_WAREHOUSE'
          : 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_SHORTAGE'
      ),
    };
  }
  if (type === 'delivery_partner') {
    return {
      title: t('DOWNLOAD_REPORTS.PREVIEW.EMPTY'),
      description: t(
        partners
          ? 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_PARTNER'
          : 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_DELIVERY'
      ),
    };
  }
  if (type === 'production') {
    let description = t('DOWNLOAD_REPORTS.PREVIEW.EMPTY_PRODUCTION');
    if (statuses && sources) {
      description = t('DOWNLOAD_REPORTS.PREVIEW.EMPTY_STATUS_SOURCE');
    } else if (statuses) {
      description = t('DOWNLOAD_REPORTS.PREVIEW.EMPTY_STATUS');
    } else if (sources) {
      description = t('DOWNLOAD_REPORTS.PREVIEW.EMPTY_HINT');
    }
    return { title: t('DOWNLOAD_REPORTS.PREVIEW.EMPTY'), description };
  }
  if (type === 'user') {
    let description = t('DOWNLOAD_REPORTS.PREVIEW.EMPTY_RANGE');
    if (employees && sources) {
      description = t('DOWNLOAD_REPORTS.PREVIEW.EMPTY_EMPLOYEE_SOURCE');
    } else if (employees) {
      description = t('DOWNLOAD_REPORTS.PREVIEW.EMPTY_EMPLOYEE');
    } else if (sources) {
      description = t('DOWNLOAD_REPORTS.PREVIEW.EMPTY_HINT');
    }
    return { title: t('DOWNLOAD_REPORTS.PREVIEW.EMPTY'), description };
  }
  return {
    title: t('DOWNLOAD_REPORTS.PREVIEW.EMPTY'),
    description: t(
      sources
        ? 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_HINT'
        : 'DOWNLOAD_REPORTS.PREVIEW.EMPTY_RANGE'
    ),
  };
};

const emptyCopy = computed(() => {
  if (props.status === 'coming_soon') {
    return {
      title: t('DOWNLOAD_REPORTS.PREVIEW.COMING_SOON'),
      description: t('DOWNLOAD_REPORTS.PREVIEW.COMING_SOON_HINT'),
      icon: 'i-lucide-clock',
    };
  }
  if (props.status === 'error') {
    return {
      title: props.errorMessage || t('DOWNLOAD_REPORTS.PREVIEW.ERROR'),
      description: '',
      icon: 'i-lucide-circle-alert',
    };
  }
  if (props.status === 'empty') {
    return {
      ...emptyPreviewCopy(),
      icon: 'i-lucide-table',
    };
  }
  return {
    title: t('DOWNLOAD_REPORTS.PREVIEW.IDLE'),
    description: t('DOWNLOAD_REPORTS.PREVIEW.IDLE_HINT'),
    icon: 'i-lucide-file-search',
  };
});
</script>

<template>
  <section
    class="relative overflow-hidden rounded-xl bg-n-solid-2 shadow outline outline-1 outline-n-container"
  >
    <div
      v-if="isLoading"
      class="flex min-h-72 flex-col items-center justify-center gap-3 px-6 py-16"
    >
      <Spinner />
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('DOWNLOAD_REPORTS.PREVIEW.LOADING') }}
      </p>
    </div>

    <template v-else-if="status === 'table'">
      <div class="flex flex-col gap-1 px-6 py-5">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ previewTitle }}
        </h3>
        <p v-if="limitNote" class="mb-0 text-sm text-n-slate-11">
          {{ limitNote }}
        </p>
        <p v-if="cacheNote" class="mb-0 text-sm text-n-slate-11">
          {{ cacheNote }}
        </p>
      </div>
      <div class="overflow-x-auto">
        <table v-if="isPaidDueReport" class="min-w-max w-full">
          <thead class="border-b border-n-container bg-n-solid-2">
            <tr>
              <th
                class="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.PAYMENT') }}
              </th>
              <th
                v-for="column in PAID_DUE_COLUMNS"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ paidDueColumnLabel(column) }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-container">
            <tr
              v-for="(row, index) in rows"
              :key="`${row.payment}-${index}`"
              class="hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <td class="whitespace-nowrap px-5 py-3 text-sm text-n-slate-12">
                {{ paymentLabel(row.payment) }}
              </td>
              <td
                v-for="column in PAID_DUE_COLUMNS"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12"
              >
                {{ formatPaidDueCell(row, column) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t border-n-container bg-n-alpha-2">
            <tr>
              <td
                class="whitespace-nowrap px-5 py-3 text-sm font-semibold text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.TOTALS') }}
              </td>
              <td
                v-for="column in PAID_DUE_COLUMNS"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-semibold tabular-nums text-n-slate-12"
              >
                {{ formatPaidDueCell(totals, column) }}
              </td>
            </tr>
          </tfoot>
        </table>
        <table v-else-if="isUserDetailReport" class="min-w-max w-full">
          <thead class="border-b border-n-container bg-n-solid-2">
            <tr>
              <th
                v-for="column in userDetailCols"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-sm font-medium text-n-slate-12"
                :class="
                  USER_DETAIL_TEXT.has(column) ? 'text-left' : 'text-right'
                "
              >
                {{ detailColumnLabel(column) }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-container">
            <tr
              v-for="row in rows"
              :key="row.order_id"
              class="hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <td
                v-for="column in userDetailCols"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-sm text-n-slate-12"
                :class="
                  USER_DETAIL_TEXT.has(column)
                    ? 'text-left'
                    : 'text-right tabular-nums'
                "
              >
                {{ formatDetailCell(row, column) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t border-n-container bg-n-alpha-2">
            <tr>
              <td
                v-for="(column, index) in userDetailCols"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-sm font-semibold text-n-slate-12"
                :class="
                  USER_DETAIL_TEXT.has(column)
                    ? 'text-left'
                    : 'text-right tabular-nums'
                "
              >
                {{ formatDetailTotal(column, index) }}
              </td>
            </tr>
          </tfoot>
        </table>
        <table v-else-if="isUserReport" class="min-w-max w-full">
          <thead class="border-b border-n-container bg-n-solid-2">
            <tr>
              <th
                class="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.AGENT') }}
              </th>
              <th
                v-for="column in userColumns"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-sm font-medium text-n-slate-12"
                :class="USER_LINE_TEXT.has(column) ? 'text-left' : 'text-right'"
              >
                {{ userColumnLabel(column) }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-container">
            <tr
              v-for="(row, index) in rows"
              :key="`${row.agent_id}-${index}`"
              class="hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <td class="whitespace-nowrap px-5 py-3 text-sm text-n-slate-12">
                {{ row.agent_name }}
              </td>
              <td
                v-for="column in userColumns"
                :key="column"
                :class="userCellClass(column)"
              >
                {{ formatUserCell(row, column) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t border-n-container bg-n-alpha-2">
            <tr>
              <td
                class="whitespace-nowrap px-5 py-3 text-sm font-semibold text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.TOTALS') }}
              </td>
              <td
                v-for="column in userColumns"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-sm font-semibold text-n-slate-12"
                :class="
                  USER_LINE_TEXT.has(column)
                    ? 'text-left'
                    : 'text-right tabular-nums'
                "
              >
                {{ formatUserCell(totals, column) }}
              </td>
            </tr>
          </tfoot>
        </table>
        <table v-else-if="isCustomerReport" class="min-w-max w-full">
          <thead class="border-b border-n-container bg-n-solid-2">
            <tr>
              <th
                v-for="column in customerLead"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-n-slate-12"
              >
                {{ customerLeadLabel(column) }}
              </th>
              <th
                v-for="column in customerMetrics"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ customerMetricLabel(column) }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-container">
            <tr
              v-for="(row, index) in rows"
              :key="`${row.customer_name}-${row.phone}-${row.product_name || ''}-${index}`"
              class="hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <td
                v-for="column in customerLead"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-sm text-n-slate-12"
              >
                {{ row[column] }}
              </td>
              <td
                v-for="column in customerMetrics"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12"
              >
                {{ formatCustomerCell(row, column) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t border-n-container bg-n-alpha-2">
            <tr>
              <td
                class="whitespace-nowrap px-5 py-3 text-sm font-semibold text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.TOTALS') }}
              </td>
              <td
                v-for="column in customerLead.slice(1)"
                :key="column"
                class="px-5 py-3"
              />
              <td
                v-for="column in customerMetrics"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-semibold tabular-nums text-n-slate-12"
              >
                {{ formatCustomerCell(totals, column) }}
              </td>
            </tr>
          </tfoot>
        </table>
        <table v-else-if="isDailyProductReport" class="min-w-max w-full">
          <thead class="border-b border-n-container bg-n-solid-2">
            <tr>
              <th
                class="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-n-slate-12"
              >
                {{ productionDateLabel }}
              </th>
              <th
                v-for="column in productionLeadColumns"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-n-slate-12"
              >
                {{ productionLeadLabel(column) }}
              </th>
              <th
                v-for="source in ORDER_SOURCES"
                :key="source"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ orderSourceLabel(source) }}
              </th>
              <th
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.TOTAL_PRODUCT_QUANTITY') }}
              </th>
              <th
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.PRODUCT_PRICE') }}
              </th>
              <th
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.TOTAL_PRODUCT_DISCOUNT') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-container">
            <tr
              v-for="(row, index) in rows"
              :key="`${row.sale_date}-${row.sku || ''}-${row.product_name}-${index}`"
              class="hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <td class="whitespace-nowrap px-5 py-3 text-sm text-n-slate-12">
                {{ row.sale_date }}
              </td>
              <td
                v-for="column in productionLeadColumns"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-sm text-n-slate-12"
              >
                {{ row[column] }}
              </td>
              <td
                v-for="source in ORDER_SOURCES"
                :key="source"
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12"
              >
                {{ sourceQty(row, source) }}
              </td>
              <td
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12"
              >
                {{ formatCount(row.qty) }}
              </td>
              <td
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12"
              >
                {{ formatDailyMoney(row.product_price) }}
              </td>
              <td
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12"
              >
                {{ formatDailyMoney(row.discount) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t border-n-container bg-n-alpha-2">
            <tr>
              <td
                class="whitespace-nowrap px-5 py-3 text-sm font-semibold text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.TOTALS') }}
              </td>
              <td
                v-for="column in productionLeadColumns"
                :key="column"
                class="px-5 py-3"
              />
              <td
                v-for="source in ORDER_SOURCES"
                :key="source"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-semibold tabular-nums text-n-slate-12"
              >
                {{ sourceQty(totals, source) }}
              </td>
              <td
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-semibold tabular-nums text-n-slate-12"
              >
                {{ formatCount(totals.qty) }}
              </td>
              <td class="px-5 py-3" />
              <td
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-semibold tabular-nums text-n-slate-12"
              >
                {{ formatDailyMoney(totals.discount) }}
              </td>
            </tr>
          </tfoot>
        </table>
        <table v-else-if="isProductionReport" class="min-w-max w-full">
          <thead class="border-b border-n-container bg-n-solid-2">
            <tr>
              <th
                class="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-n-slate-12"
              >
                {{ productionDateLabel }}
              </th>
              <th
                v-for="column in productionLeadColumns"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-n-slate-12"
              >
                {{ productionLeadLabel(column) }}
              </th>
              <th
                v-for="column in PRODUCTION_BASE_COLUMNS"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ productionMetricLabel(column) }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-container">
            <tr
              v-for="(row, index) in rows"
              :key="`${row.sale_date}-${row.sku || ''}-${row.product_name}-${index}`"
              class="hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <td class="whitespace-nowrap px-5 py-3 text-sm text-n-slate-12">
                {{ row.sale_date }}
              </td>
              <td
                v-for="column in productionLeadColumns"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-sm text-n-slate-12"
              >
                {{ row[column] }}
              </td>
              <td
                v-for="column in PRODUCTION_BASE_COLUMNS"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12"
              >
                {{ formatProductionCell(row, column) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t border-n-container bg-n-alpha-2">
            <tr>
              <td
                class="whitespace-nowrap px-5 py-3 text-sm font-semibold text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.TOTALS') }}
              </td>
              <td
                v-for="column in productionLeadColumns"
                :key="column"
                class="px-5 py-3"
              />
              <td
                v-for="column in PRODUCTION_BASE_COLUMNS"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-semibold tabular-nums text-n-slate-12"
              >
                {{ formatProductionCell(totals, column) }}
              </td>
            </tr>
          </tfoot>
        </table>
        <table v-else-if="isInventoryReport" class="min-w-max w-full">
          <thead class="border-b border-n-container bg-n-solid-2">
            <tr>
              <th
                v-for="column in inventoryLead"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-n-slate-12"
              >
                {{ inventoryLeadLabel(column) }}
              </th>
              <th
                v-for="column in inventoryMetrics"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ inventoryMetricLabel(column) }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-container">
            <tr
              v-for="(row, index) in rows"
              :key="`${row.sale_date || ''}-${row.product_name}-${row.sku}-${row.expires_on || ''}-${index}`"
              class="hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <td
                v-for="column in inventoryLead"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-sm text-n-slate-12"
              >
                {{ row[column] }}
              </td>
              <td
                v-for="column in inventoryMetrics"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12"
              >
                {{ formatCount(row[column]) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t border-n-container bg-n-alpha-2">
            <tr>
              <td
                class="whitespace-nowrap px-5 py-3 text-sm font-semibold text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.TOTALS') }}
              </td>
              <td
                v-for="column in inventoryLead.slice(1)"
                :key="column"
                class="px-5 py-3"
              />
              <td
                v-for="column in inventoryMetrics"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-semibold tabular-nums text-n-slate-12"
              >
                {{ formatCount(totals[column]) }}
              </td>
            </tr>
          </tfoot>
        </table>
        <table v-else-if="isDeliveryReport" class="min-w-max w-full">
          <thead class="border-b border-n-container bg-n-solid-2">
            <tr>
              <th
                v-for="column in DELIVERY_SHIPMENT_LEAD"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-n-slate-12"
              >
                {{ deliveryLeadLabel(column) }}
              </th>
              <th
                v-for="column in DELIVERY_SHIPMENT_METRICS"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ deliveryMetricLabel(column) }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-container">
            <tr
              v-for="(row, index) in rows"
              :key="`${row.partner_name}-${row.provider}-${index}`"
              class="hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <td
                v-for="column in DELIVERY_SHIPMENT_LEAD"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-sm text-n-slate-12"
              >
                {{ row[column] }}
              </td>
              <td
                v-for="column in DELIVERY_SHIPMENT_METRICS"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12"
              >
                {{ formatCount(row[column]) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t border-n-container bg-n-alpha-2">
            <tr>
              <td
                class="whitespace-nowrap px-5 py-3 text-sm font-semibold text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.TOTALS') }}
              </td>
              <td
                v-for="column in DELIVERY_SHIPMENT_LEAD.slice(1)"
                :key="column"
                class="px-5 py-3"
              />
              <td
                v-for="column in DELIVERY_SHIPMENT_METRICS"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-semibold tabular-nums text-n-slate-12"
              >
                {{ formatCount(totals[column]) }}
              </td>
            </tr>
          </tfoot>
        </table>
        <table v-else class="min-w-max w-full">
          <thead class="border-b border-n-container bg-n-solid-2">
            <tr>
              <th
                class="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.ORIGIN') }}
              </th>
              <th
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.ORDERS') }}
              </th>
              <th
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.AMOUNT') }}
              </th>
              <th
                v-for="column in originMetricColumns"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-n-slate-12"
              >
                {{ originMetricLabel(column) }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-container">
            <tr
              v-for="(row, index) in rows"
              :key="`${row.origin}-${index}`"
              class="hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <td class="whitespace-nowrap px-5 py-3 text-sm text-n-slate-12">
                {{ originLabel(row.origin) }}
              </td>
              <td
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12"
              >
                {{ formatCount(row.orders) }}
              </td>
              <td
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-12"
              >
                {{ formatAmount(row.amount) }}
              </td>
              <td
                v-for="column in originMetricColumns"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm tabular-nums text-n-slate-11"
              >
                {{ formatOriginMetric(row, column) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="border-t border-n-container bg-n-alpha-2">
            <tr>
              <td
                class="whitespace-nowrap px-5 py-3 text-sm font-semibold text-n-slate-12"
              >
                {{ t('DOWNLOAD_REPORTS.TABLE.TOTALS') }}
              </td>
              <td
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-semibold tabular-nums text-n-slate-12"
              >
                {{ formatCount(totals.orders) }}
              </td>
              <td
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-semibold tabular-nums text-n-slate-12"
              >
                {{ formatAmount(totals.amount) }}
              </td>
              <td
                v-for="column in originMetricColumns"
                :key="column"
                class="whitespace-nowrap px-5 py-3 text-right text-sm font-semibold tabular-nums text-n-slate-12"
              >
                {{ formatOriginMetric(totals, column) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <PaginationFooter
        v-if="showPagination"
        :current-page="currentPage"
        :total-items="totalRows"
        :items-per-page="perPage"
        @update:current-page="emit('pageChange', $event)"
      />
    </template>

    <template v-else>
      <div v-if="status === 'empty'" class="flex flex-col gap-1 px-6 pt-5">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ previewTitle }}
        </h3>
        <p v-if="limitNote" class="mb-0 text-sm text-n-slate-11">
          {{ limitNote }}
        </p>
        <p v-if="cacheNote" class="mb-0 text-sm text-n-slate-11">
          {{ cacheNote }}
        </p>
      </div>
      <CsatEmptyState
        :title="emptyCopy.title"
        :description="emptyCopy.description"
        :icon="emptyCopy.icon"
      />
    </template>
  </section>
</template>
